import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, del, uploadFile, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { Avatar } from "@/components/common/Avatar";
import { Button } from "@/components/ui/button";
import { UploadProgress } from "@/components/common/UploadProgress";
import { PenTool, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

function SignatureRow({ emp, canEdit }) {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [bust, setBust] = useState(0);

  const upload = useMutation({
    mutationFn: (fd) => uploadFile(`/hrms/employees/${emp.id}/signature`, fd, setProgress),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["hrms-directory"] }); toast.success("Signature uploaded"); setFile(null); setProgress(null); setBust((b) => b + 1); },
    onError: (e) => { setProgress(null); toast.error(e?.response?.data?.detail || "Could not upload signature"); },
  });
  const remove = useMutation({
    mutationFn: () => del(`/hrms/employees/${emp.id}/signature`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["hrms-directory"] }); toast.success("Signature removed"); },
  });

  return (
    <div data-testid={`sig-row-${emp.id}`} className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
      <div className="flex min-w-0 items-center gap-2.5">
        <Avatar user={emp} size="sm" />
        <div className="min-w-0">
          <div className="truncate font-medium">{emp.name}</div>
          <div className="truncate text-xs text-muted-foreground">{emp.designation || "—"}</div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {emp.signature_r2_key ? (
          <img src={`${API}/hrms/employees/${emp.id}/signature?v=${bust}`} alt={`${emp.name}'s signature`}
            data-testid={`sig-preview-${emp.id}`} className="h-10 max-w-[140px] rounded border border-border bg-white object-contain p-1" />
        ) : (
          <span className="text-xs text-muted-foreground">No signature on file</span>
        )}
        {canEdit && (
          <>
            <input type="file" accept="image/png,image/jpeg" data-testid={`sig-file-${emp.id}`}
              className="hidden" id={`sig-input-${emp.id}`}
              onChange={(e) => {
                const f = e.target.files?.[0] || null;
                setFile(f);
                if (f) {
                  const fd = new FormData();
                  fd.append("file", f);
                  upload.mutate(fd);
                }
              }} />
            <Button size="sm" variant="outline" data-testid={`sig-upload-btn-${emp.id}`} disabled={upload.isPending}
              onClick={() => document.getElementById(`sig-input-${emp.id}`).click()}>
              <Upload className="mr-1.5 h-3.5 w-3.5" />{emp.signature_r2_key ? "Replace" : "Upload"}
            </Button>
            {emp.signature_r2_key && (
              <ConfirmDelete testid={`del-sig-${emp.id}`} title={`Remove ${emp.name}'s signature?`}
                description="Any document template pointing at this employee as signatory will stop stamping a signature until a new image is uploaded."
                trigger={<button className="text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                onConfirm={() => remove.mutate()} />
            )}
          </>
        )}
      </div>
      {progress != null && <div className="w-24"><UploadProgress percent={progress} /></div>}
    </div>
  );
}

export default function SignatureDirectory() {
  const { can } = useApp();
  const { data: employees, isLoading } = useQuery({ queryKey: ["hrms-directory"], queryFn: () => get("/hrms/directory") });
  const canEdit = can("hrms", "edit");

  return (
    <div>
      <PageHeader title="Signature Directory" subtitle="Each employee's signature image, used to stamp authorized-signatory documents generated from HRMS templates." />
      {isLoading ? <Skeleton className="h-64" /> : !employees?.length ? (
        <EmptyState icon={PenTool} title="No employees yet" />
      ) : (
        <Section>
          <div className="divide-y divide-border">
            {employees.map((emp) => <SignatureRow key={emp.id} emp={emp} canEdit={canEdit} />)}
          </div>
        </Section>
      )}
    </div>
  );
}
